import {
  Avatar,
  Card,
  Flex,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { Pen, Trash2 } from "lucide-react";
import styles from "./styles.module.scss";

import { PlusOutlined } from "@ant-design/icons";
import { ToastService } from "@shared/lib/services";
import { IBrand, IGetOrganisationPermissions } from "@shared/lib/types";
import { IBrandOrganisation } from "@shared/lib/types/brandOrganisation";
import { Button } from "@shared/ui/Button";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import { useEffect, useState } from "react";
import useDeleteUserFromOrganisation from "../lib/hooks/useDeleteUserFromOrganisation";
import OrganisationAccountManagerDetails from "./OrganisationAccountManagerDetails";

export const TeamTab = ({
  organisation,
  organisationId,
}: {
  organisation: IBrandOrganisation | undefined;
  organisationId: string;
}) => {
  const [modal, setModal] = useState<{
    organisation: IBrandOrganisation | null;
    open: boolean;
    userPermissions: IGetOrganisationPermissions | null;
    isEdit: boolean;
    brand: IBrand | null;
  }>({
    organisation: null,
    brand: null,
    open: false,
    userPermissions: null,
    isEdit: false,
  });

  const {
    deleteUserFromOrganisation,
    deleteUserError,
    isUserDeleted,
    isUserDeleting,
  } = useDeleteUserFromOrganisation();
  useEffect(() => {
    if (isUserDeleted) {
      ToastService.success("User deleted successfully");
    } else if (deleteUserError) {
      ToastService.error(deleteUserError?.message);
    }
  }, [isUserDeleted, deleteUserError]);

  const getEditModalTitle = () => {
    const prefix = modal.isEdit ? "Edit" : "Add";
    let postfix = "";
    if (!modal.userPermissions) {
      postfix = "Brand";
    } else if (modal.userPermissions?.userType === "brand-user") {
      postfix = "Employee";
    } else {
      postfix = "Member";
    }
    return `${prefix} ${postfix}`;
  };
  return (
    <div className={styles.tabPane}>
      <Card
        title="Allocated Team"
        extra={
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() =>
              setModal({
                open: true,
                userPermissions: {
                  userType: "admin-user",
                } as IGetOrganisationPermissions,
                organisation: organisation || null,
                isEdit: false,
                brand: null,
              })
            }
          >
            Add Team Member
          </Button>
        }
        className={styles.card}
        bodyStyle={{ padding: "1px 0 0 0" }}
      >
        <Table
          dataSource={organisation?.permission?.filter(
            (item) => item.userType !== "brand-user"
          )}
          rowKey={(record) => record.user._id}
          pagination={false}
          className={styles.table}
          columns={[
            {
              title: "Member",
              key: "member",
              minWidth: 300,
              render: (_, record) => (
                <Flex align="center" gap={12}>
                  <Avatar
                    shape="square"
                    size={40}
                    style={{ width: 40, height: 40 }}
                    className={styles.teamAvatar}
                  >
                    {record.user?.firstName?.[0]}
                  </Avatar>
                  <Flex vertical>
                    <Typography.Text strong>
                      {record.user?.firstName} {record.user?.lastName}
                    </Typography.Text>
                    <Typography.Text
                      type="secondary"
                      className={styles.secondaryText}
                    >
                      {record.user?.emailId}
                    </Typography.Text>
                  </Flex>
                </Flex>
              ),
            },
            {
              title: "Role",
              dataIndex: ["user", "role"],
              key: "role",
              render: (role) => <Tag>{role}</Tag>,
            },
            {
              title: "Contact",
              dataIndex: ["user", "emailId"],
              key: "contact",
            },
            {
              title: "Brand",
              dataIndex: "brand",
              key: "brand",
              width: 150,
              render: (_, record) => (
                <Flex vertical gap={10}>
                  {organisation?.brands
                    ?.filter((val) => record.brandIds?.includes(val._id))
                    .slice(0, 2)
                    .map((val) => (
                      <Tag key={val._id} style={{ width: "fit-content" }}>
                        <TextWithTooltip text={val.name} limit={15} />
                      </Tag>
                    ))}
                  {organisation?.brands &&
                  organisation?.brands?.filter((val) =>
                    record.brandIds?.includes(val._id)
                  ).length > 2 ? (
                    <Tooltip
                      title={organisation?.brands
                        ?.map((val) => val.name)
                        .join(", ")}
                    >
                      <Tag style={{ width: "fit-content" }}>
                        +
                        {organisation?.brands?.filter((val) =>
                          record.brandIds?.includes(val._id)
                        ).length - 2}{" "}
                        more
                      </Tag>
                    </Tooltip>
                  ) : null}
                </Flex>
              ),
            },
            {
              title: "Actions",
              key: "actions",
              align: "right",
              render: (_, record) => (
                <Space>
                  <Button
                    type="text"
                    onClick={() => {
                      setModal({
                        open: true,
                        userPermissions: record,
                        organisation: organisation || null,
                        isEdit: true,
                        brand: null,
                      });
                    }}
                    icon={<Pen size={16} />}
                  />
                  <Button
                    type="text"
                    danger
                    onClick={() => {
                      deleteUserFromOrganisation({
                        userId: record.user?._id || "",
                        brandOrganisationId: organisation?._id || "",
                      });
                    }}
                    icon={<Trash2 size={16} />}
                  />
                </Space>
              ),
            },
          ]}
          scroll={{
            x: "auto",
          }}
        />
      </Card>
      <Modal
        open={modal.open}
        title={getEditModalTitle()}
        destroyOnClose
        onCancel={() =>
          setModal({
            open: false,
            userPermissions: null,
            organisation: null,
            isEdit: false,
            brand: null,
          })
        }
        footer={null}
      >
        {/* <OrganisationUserForm
          organisationId={modal.organisation?._id || ""}
          userPermissions={modal.userPermissions}
          isEdit={modal.isEdit}
          onCancel={() =>
            setModal({
              open: false,
              userPermissions: null,
              organisation: null,
              isEdit: false,
              brand: null,
            })
          }
        /> */}

        <OrganisationAccountManagerDetails
          organisationId={modal.organisation?._id || ""}
          userPermissions={modal.userPermissions}
          isEdit={modal.isEdit}
          onCancel={() =>
            setModal({
              open: false,
              userPermissions: null,
              organisation: null,
              isEdit: false,
              brand: null,
            })
          }
        />
      </Modal>
    </div>
  );
};
